import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AiHostsPage — P1-AI07.
 *
 * Operator surface for the AI host management subsystem shipped in
 * P1-AI01..06. Lives at `/platform/ai-hosts` inside any admin SPA that
 * mounts `<NexusAdminRoutes>` (AquaStar-Admin today, future K-Stack-Admin
 * tomorrow).
 *
 * Talks exclusively to:
 *   GET    /v1/admin/ai-hosts                          list (filter by capability/status)
 *   POST   /v1/admin/ai-hosts                          register
 *   PATCH  /v1/admin/ai-hosts/:id                      edit + ownership-flip
 *   DELETE /v1/admin/ai-hosts/:id                      archive (soft)
 *   GET    /v1/admin/ai-hosts/:id/workspaces           per-host detail (HostDetailDrawer)
 *   POST   /v1/admin/ai-hosts/:id/provision            stage-scoped provisioning
 *   POST   /v1/admin/ai-hosts/:id/ollama/install       SSH dispatch (DeployOllamaModal)
 *   GET    /v1/admin/ai-hosts/:id/ollama/install/status status poll
 *   GET    /v1/admin/ai-hosts/:id/ollama/models        proxy /api/tags
 *   POST   /v1/admin/ai-hosts/:id/ollama/pull          pull model
 *   DELETE /v1/admin/ai-hosts/:id/ollama/models/:name  delete model
 *   GET    /v1/admin/ai-chat-providers                 providers list
 *   POST   /v1/admin/ai-chat-providers                 add provider
 *   PATCH  /v1/admin/ai-chat-providers/:id             edit + enable/default toggle
 *   DELETE /v1/admin/ai-chat-providers/:id             hard delete
 *
 * Page layout:
 *   ┌──── filters ───── refresh · Add host ─────────────────────────┐
 *   │ Hosts table (one row per ai_hosts)                            │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌──── Chat providers ───────────────── Add provider ─────────────┐
 *   │ Providers table                                                │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Modals: HostEditModal · HostDetailDrawer · DeployOllamaModal ·
 * ProviderEditModal · two ConfirmModal variants (archive host /
 * delete provider).
 *
 * Pagination intentionally absent — host count is expected to stay
 * small (typically <10 even in multi-stage deploys).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
import { AiServicePanel, AppsAiResolutionPanel, LocalPlumbing, ReadOnlyWhen, RoutedToNexusAiNotice } from './ai-hosts/AiServicePanel.js';
import { HostsTable } from './ai-hosts/HostsTable.js';
import { HostEditModal, emptyHostDraft, hostToDraft, isValidHostName, draftToCreate, draftToPatch, errorFieldKey as hostErrorFieldKey, } from './ai-hosts/HostEditModal.js';
import { HostDetailDrawer } from './ai-hosts/HostDetailDrawer.js';
import { DeployOllamaModal } from './ai-hosts/DeployOllamaModal.js';
import { ProvidersTable } from './ai-hosts/ProvidersTable.js';
import { ExecutionPolicyPanel } from './ai-hosts/ExecutionPolicyPanel.js';
import { WorkerRecoveryPanel } from './ai-hosts/WorkerRecoveryPanel.js';
import { AppPriorityPanel } from './ai-hosts/AppPriorityPanel.js';
import { RunnersPanel } from './ai-hosts/RunnersPanel.js';
import { SubscriptionUsagePanel } from './ai-hosts/SubscriptionUsagePanel.js';
import { ImageServicesPanel } from './ai-hosts/ImageServicesPanel.js';
import { VoiceServicesPanel } from './ai-hosts/VoiceServicesPanel.js';
import { FallbackPathsPanel } from './ai-hosts/FallbackPathsPanel.js';
import { ProviderQuotaBannerLoader, ProviderQuotaPanel } from './ai-hosts/ProviderQuotaPanel.js';
import { ProviderEditModal, emptyProviderDraft, providerToDraft, isValidProviderName, parseConfigText, draftToCreateProvider, draftToPatchProvider, providerErrorFieldKey, } from './ai-hosts/ProviderEditModal.js';
import { AllowedModelsPicker, } from './ai-hosts/AllowedModelsPicker.js';
const SECTION_TITLE = {
    all: { title: 'AI hosts', blurb: 'Registered AI-capable machines (Ollama hosts + Cloud-CLI hosts). Each host carries one or more capabilities, an optional workspace root per stage, and zero or more chat providers that route to it. Edit any row to take ownership — the deploy script will skip UI-managed rows on the next re-deploy.' },
    service: { title: 'AI service', blurb: 'Who serves this instance\'s AI — the fleet managed here or another instance\'s endpoint — how each app resolves, the fallback path per kind of work, the execution policy, and which machine handles images and speech.' },
    hosts: { title: 'AI hosts & models', blurb: 'Registered AI-capable machines (Ollama hosts + Cloud-CLI hosts): capabilities, installed models, pulls and removes, workspaces per stage, worker recovery. Chat providers that route to a host live under Providers.' },
    providers: { title: 'Chat providers', blurb: 'One row per provider name an app or user can ask for: kind, host, model, label, default. A name is immutable — change the label to rename what people see.' },
    agents: { title: 'Agents', blurb: 'Agent runners (headless workers with their own service account that take agent_control jobs) and the agent-control hosts that registered themselves at sign-in.' },
};
export function AiHostsPage({ section = 'all' } = {}) {
    const fetcher = useNexusFetch();
    const show = (...of) => section === 'all' || of.includes(section);
    // -------------------- filters --------------------------------------
    const [capabilityFilter, setCapabilityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    // -------------------- data -----------------------------------------
    const [hosts, setHosts] = useState(null);
    const [providers, setProviders] = useState(null);
    const [loadHostsError, setLoadHostsError] = useState(null);
    const [loadProvidersError, setLoadProvidersError] = useState(null);
    const [workspaceCounts, setWorkspaceCounts] = useState(() => new Map());
    const [toast, setToast] = useState(null);
    // Per-app provider pin (2026-09-03): which chat provider serves the
    // selected app. null = inherit the instance default. Loaded whenever
    // the App context changes; saved via PUT /v1/admin/ai-hosts/app-provider.
    const [appPin, setAppPin] = useState(null);
    const [appPinDraft, setAppPinDraft] = useState(null);
    const [appPinBusy, setAppPinBusy] = useState(false);
    // 2026-09-08: the GET used to fail silently into "inherit", and a
    // picked-but-unsaved provider looked exactly like a saved one (Erwin
    // read "ollama-mac-default" in the select while the desktop was still
    // pinned). Track load failure + render the SAVED value explicitly.
    const [appPinLoadError, setAppPinLoadError] = useState(null);
    // Dynamic Claude model catalog for the agent_control Model dropdown
    // (2026-09-03): CLI aliases + the live Anthropic model list.
    const [claudeModels, setClaudeModels] = useState([]);
    // -------------------- P1-AI12 app picker + allowed-models -----------
    //
    // Where the "current app context" comes from:
    //   1. URL query param `?app=<appId>` if present and matches an
    //      active app.
    //   2. Otherwise the first active app from /v1/admin/apps.
    //   3. Otherwise null (the dropdown shows "no app — free-form models").
    //
    // The selected app gates two things:
    //   - The Model field on the provider edit modal (becomes a <select>
    //     when (selectedApp, ollama-host) has whitelist rows).
    //   - The AllowedModelsPicker modal opened from the row's
    //     "Allowed models" button.
    const [appsList, setAppsList] = useState(null);
    // NEXUS-1345: the AI service card decides whether the fleet plumbing below is live or folded away.
    // undefined until the card has answered (NEXUS-1527: the read-only sections stay disabled meanwhile); null = no AI service mode here.
    const [aiService, setAiService] = useState(undefined);
    // "The Gateway default decides" (2026-10-28): in remote mode per-app routing is not used for inference.
    const routingManagedElsewhere = aiService?.mode === 'remote';
    const onAiServiceMode = useCallback((v) => setAiService(v), []);
    // NEXUS-1527 (Erwin 2026-10-29): in remote mode AI is routed to Nexus AI, so the host and provider pages are
    // read-only. Agent runners stay live (they are local machines, not inference routing), and so does AI › Service.
    const plumbingReadOnly = (routingManagedElsewhere || aiService === undefined) && (section === 'hosts' || section === 'providers');
    const [selectedAppId, setSelectedAppId] = useState(() => {
        if (typeof window === 'undefined')
            return null;
        const sp = new URLSearchParams(window.location.search);
        const v = sp.get('app');
        return v && v.trim() ? v.trim() : null;
    });
    /** Per-host map: hostId -> enabled allowed-model names for the
     *  currently selected app. Refreshed whenever selectedAppId or hosts
     *  changes. Best-effort: per-host failures land as empty arrays. */
    const [allowedByHost, setAllowedByHost] = useState(() => new Map());
    const [allowedPicker, setAllowedPicker] = useState({
        kind: 'closed',
    });
    // 30s ticker so "last seen 3m ago" labels age. Cheaper than 1s — host
    // last_seen updates are much less frequent than scheduled-task runs.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(id);
    }, []);
    const [hostEdit, setHostEdit] = useState({ kind: 'closed' });
    const [hostDraft, setHostDraft] = useState(emptyHostDraft);
    const [hostSubmitting, setHostSubmitting] = useState(false);
    const [hostFormBanner, setHostFormBanner] = useState(null);
    const [hostFieldErrors, setHostFieldErrors] = useState({});
    const [hostGeneralErrors, setHostGeneralErrors] = useState([]);
    const [detail, setDetail] = useState({ kind: 'closed' });
    const [deploy, setDeploy] = useState({ kind: 'closed' });
    const [archive, setArchive] = useState({ kind: 'closed' });
    const [providerEdit, setProviderEdit] = useState({
        kind: 'closed',
    });
    const [providerDraft, setProviderDraft] = useState(emptyProviderDraft);
    const [providerSubmitting, setProviderSubmitting] = useState(false);
    const [providerFormBanner, setProviderFormBanner] = useState(null);
    const [providerFieldErrors, setProviderFieldErrors] = useState({});
    const [providerGeneralErrors, setProviderGeneralErrors] = useState([]);
    const [llmProviderCreds, setLlmProviderCreds] = useState([]);
    // Picked credential for the currently-open provider modal. Hoisted
    // out of providerDraft.configText so the dropdown is the source of
    // truth — configText only carries the rest of the config blob.
    const [pickedCredentialId, setPickedCredentialId] = useState('');
    const [agentControlHosts, setAgentControlHosts] = useState([]);
    const [pickedAgentControlHostId, setPickedAgentControlHostId] = useState('');
    const [providerDelete, setProviderDelete] = useState({
        kind: 'closed',
    });
    // -------------------- toast plumbing -------------------------------
    const fireToast = useCallback((kind, message) => {
        setToast((prev) => ({ kind, message, seq: (prev?.seq ?? 0) + 1 }));
    }, []);
    useEffect(() => {
        if (!toast)
            return undefined;
        const id = window.setTimeout(() => setToast(null), 6000);
        return () => window.clearTimeout(id);
    }, [toast]);
    // -------------------- data loaders ---------------------------------
    const loadHosts = useCallback(async () => {
        setLoadHostsError(null);
        try {
            const params = new URLSearchParams();
            if (capabilityFilter !== 'all' &&
                (capabilityFilter === 'ollama-host' || capabilityFilter === 'cli-host')) {
                params.set('capability', capabilityFilter);
            }
            if (statusFilter !== 'all')
                params.set('status', statusFilter);
            const qs = params.toString();
            const res = await fetcher(`/v1/admin/ai-hosts${qs ? `?${qs}` : ''}`);
            let rows = res?.hosts ?? [];
            // 'hybrid' is a client-side derived filter — host has both caps.
            if (capabilityFilter === 'hybrid') {
                rows = rows.filter((h) => (h.capabilities ?? []).includes('ollama-host') &&
                    (h.capabilities ?? []).includes('cli-host'));
            }
            setHosts(rows);
        }
        catch (e) {
            setHosts([]);
            setLoadHostsError(e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'failed to load hosts');
        }
    }, [capabilityFilter, fetcher, statusFilter]);
    const loadProviders = useCallback(async () => {
        setLoadProvidersError(null);
        try {
            const res = await fetcher('/v1/admin/ai-chat-providers');
            setProviders(res?.providers ?? []);
        }
        catch (e) {
            setProviders([]);
            setLoadProvidersError(e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'failed to load providers');
        }
    }, [fetcher]);
    // Workspace counts: best-effort, one GET per host. Failures fall back
    // to '—' in the table; we don't block the page.
    const loadWorkspaceCounts = useCallback(async (rows) => {
        const next = new Map();
        await Promise.all(rows.map(async (h) => {
            try {
                const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(h.id)}/workspaces`);
                next.set(h.id, (res?.roots ?? []).length);
            }
            catch {
                // ignore per-host failure
            }
        }));
        setWorkspaceCounts(next);
    }, [fetcher]);
    // P1-AI12 — fetch the list of apps so the picker has options.
    // Best-effort: an empty list disables the per-app model gating UI but
    // leaves the rest of the page working normally.
    const loadApps = useCallback(async () => {
        try {
            const res = await fetcher('/v1/admin/apps');
            const rows = res?.apps ?? [];
            setAppsList(rows);
        }
        catch {
            setAppsList([]);
        }
    }, [fetcher]);
    // Load allowed-models for every ollama host under the current app.
    // One GET per host. Failures fall back to empty arrays so the modal
    // gracefully falls back to free-form input.
    const loadAllowedByHost = useCallback(async (rows, appId) => {
        const next = new Map();
        await Promise.all(rows
            .filter((h) => (h.capabilities ?? []).includes('ollama-host'))
            .map(async (h) => {
            try {
                const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(h.id)}/allowed-models?app=${encodeURIComponent(appId)}`);
                const names = (res?.allowedModels ?? [])
                    .filter((m) => m.enabled)
                    .map((m) => m.modelName);
                next.set(h.id, names);
            }
            catch {
                next.set(h.id, []);
            }
        }));
        setAllowedByHost(next);
    }, [fetcher]);
    useEffect(() => {
        void loadHosts();
    }, [loadHosts]);
    useEffect(() => {
        void loadProviders();
    }, [loadProviders]);
    useEffect(() => {
        void loadApps();
    }, [loadApps]);
    // When the apps list lands AND we don't yet have a selected app
    // (because the URL didn't pin one), fall back to the first ACTIVE app.
    useEffect(() => {
        if (appsList === null)
            return;
        if (selectedAppId)
            return;
        const firstActive = appsList.find((a) => a.status === 'active');
        if (firstActive)
            setSelectedAppId(firstActive.appId);
    }, [appsList, selectedAppId]);
    // Re-fetch allowed-models whenever the selected app OR the host list
    // changes. Both inputs together are the "dependency" of the per-host
    // model dropdown gating.
    useEffect(() => {
        if (!selectedAppId) {
            setAllowedByHost(new Map());
            return;
        }
        if (hosts && hosts.length > 0) {
            void loadAllowedByHost(hosts, selectedAppId);
        }
    }, [hosts, selectedAppId, loadAllowedByHost]);
    useEffect(() => {
        if (!selectedAppId) {
            setAppPin(null);
            setAppPinDraft(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetcher(`/v1/admin/ai-hosts/app-provider?app=${encodeURIComponent(selectedAppId)}`);
                if (!cancelled) {
                    setAppPin(res?.providerName ?? null);
                    setAppPinDraft(res?.providerName ?? null);
                    setAppPinLoadError(null);
                }
            }
            catch (e) {
                if (!cancelled) {
                    setAppPin(null);
                    setAppPinDraft(null);
                    setAppPinLoadError(e instanceof Error ? e.message : 'could not load the saved routing');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedAppId, fetcher]);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetcher('/v1/admin/ai-hosts/claude-models');
                if (!cancelled)
                    setClaudeModels(res?.models ?? []);
            }
            catch {
                if (!cancelled)
                    setClaudeModels([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fetcher]);
    const saveAppPin = useCallback(async () => {
        if (!selectedAppId)
            return;
        setAppPinBusy(true);
        try {
            const res = await fetcher(`/v1/admin/ai-hosts/app-provider?app=${encodeURIComponent(selectedAppId)}`, {
                method: 'PUT',
                body: JSON.stringify({ providerName: appPinDraft }),
            });
            setAppPin(res?.providerName ?? null);
            setAppPinDraft(res?.providerName ?? null);
            fireToast('success', res?.providerName
                ? `"${selectedAppId}" now uses provider "${res.providerName}" (other apps unaffected).`
                : `"${selectedAppId}" now inherits the instance default provider.`);
        }
        catch (e) {
            fireToast('danger', e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'failed to save app provider');
        }
        finally {
            setAppPinBusy(false);
        }
    }, [selectedAppId, appPinDraft, fetcher, fireToast]);
    useEffect(() => {
        if (hosts && hosts.length > 0) {
            void loadWorkspaceCounts(hosts);
        }
    }, [hosts, loadWorkspaceCounts]);
    // -------------------- host edit handlers ---------------------------
    const openCreateHost = useCallback(() => {
        setHostDraft(emptyHostDraft());
        setHostFormBanner(null);
        setHostFieldErrors({});
        setHostGeneralErrors([]);
        setHostEdit({ kind: 'create' });
    }, []);
    const openEditHost = useCallback((row) => {
        setHostDraft(hostToDraft(row));
        setHostFormBanner(null);
        setHostFieldErrors({});
        setHostGeneralErrors([]);
        setHostEdit({ kind: 'edit', row });
    }, []);
    const closeHostEdit = useCallback(() => {
        if (hostSubmitting)
            return;
        setHostEdit({ kind: 'closed' });
        setHostFormBanner(null);
        setHostFieldErrors({});
        setHostGeneralErrors([]);
    }, [hostSubmitting]);
    const updateHostDraftField = useCallback((key, value) => {
        setHostDraft((d) => ({ ...d, [key]: value }));
        setHostFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const applyHostServerErrors = useCallback((err) => {
        if (err.code === 'INVALID_REQUEST' && Array.isArray(err.errors)) {
            const byField = {};
            const general = [];
            for (const e of err.errors) {
                const key = hostErrorFieldKey(e.field ?? e.path);
                if (key) {
                    (byField[key] ??= []).push(e.message);
                }
                else {
                    general.push(e.message);
                }
            }
            setHostFieldErrors(byField);
            setHostGeneralErrors(general);
            setHostFormBanner(err.message || 'host shape failed validation');
            return;
        }
        if (err.code === 'INVALID_REQUEST' && err.body?.field) {
            const fe = err.body;
            const key = hostErrorFieldKey(fe.field);
            if (key) {
                setHostFieldErrors({ [key]: [err.message] });
            }
            else {
                setHostGeneralErrors([err.message]);
            }
            setHostFormBanner(err.message);
            return;
        }
        if (err.status === 409) {
            setHostFormBanner(err.message || 'name already in use');
            return;
        }
        setHostFormBanner(`${err.code}: ${err.message}`);
    }, []);
    const handleHostSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (hostEdit.kind === 'closed')
            return;
        const name = hostDraft.name.trim();
        if (!isValidHostName(name)) {
            setHostFieldErrors((fe) => ({
                ...fe,
                name: ['Must match /^[a-z0-9][a-z0-9._-]{1,99}$/i.'],
            }));
            return;
        }
        setHostSubmitting(true);
        setHostFormBanner(null);
        setHostFieldErrors({});
        setHostGeneralErrors([]);
        try {
            if (hostEdit.kind === 'create') {
                await fetcher('/v1/admin/ai-hosts', {
                    method: 'POST',
                    json: draftToCreate(hostDraft),
                });
                fireToast('success', `Registered host "${name}".`);
            }
            else {
                const patch = draftToPatch(hostDraft, hostEdit.row);
                if (Object.keys(patch).length === 0) {
                    setHostEdit({ kind: 'closed' });
                    return;
                }
                await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(hostEdit.row.id)}`, { method: 'PATCH', json: patch });
                const wasDeployManaged = hostEdit.row.deployScriptManaged;
                fireToast('success', wasDeployManaged
                    ? `Saved "${name}". Ownership flipped to ui — deploy script will skip this row.`
                    : `Saved "${name}".`);
            }
            setHostEdit({ kind: 'closed' });
            await loadHosts();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                applyHostServerErrors(err);
            }
            else {
                setHostFormBanner(err instanceof Error ? err.message : 'request failed (unknown)');
            }
        }
        finally {
            setHostSubmitting(false);
        }
    }, [applyHostServerErrors, fetcher, fireToast, hostDraft, hostEdit, loadHosts]);
    // -------------------- archive --------------------------------------
    const openArchive = useCallback((row) => {
        setArchive({ kind: 'open', row, submitting: false });
    }, []);
    const closeArchive = useCallback(() => {
        setArchive((a) => a.kind === 'open' && a.submitting ? a : { kind: 'closed' });
    }, []);
    const handleArchive = useCallback(async () => {
        if (archive.kind !== 'open')
            return;
        const row = archive.row;
        setArchive({ kind: 'open', row, submitting: true });
        try {
            await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(row.id)}`, {
                method: 'DELETE',
            });
            fireToast('success', `Archived "${row.name}".`);
            setArchive({ kind: 'closed' });
            await loadHosts();
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'archive failed';
            fireToast('danger', msg);
            setArchive({ kind: 'open', row, submitting: false });
        }
    }, [archive, fetcher, fireToast, loadHosts]);
    // -------------------- provider handlers ----------------------------
    // P1-AI14 — fetch llm_provider credentials so the modal can render a
    // dropdown. Tolerant of 404 (older gateway without the credentials
    // module): we silently keep the list empty and the modal hides the
    // field via its existing `credentials &&` guard.
    const loadLlmProviderCreds = useCallback(async () => {
        try {
            const res = await fetcher('/v1/admin/credentials?type=llm_provider');
            setLlmProviderCreds(res.credentials ?? []);
        }
        catch {
            // Silently ignore — keeps back-compat with gateways that don't
            // serve the credentials module yet.
            setLlmProviderCreds([]);
        }
    }, [fetcher]);
    // Agent-Control hosts for the kind='agent_control' dropdown. Tolerant
    // of 404/501 (older gateway or agent_control DB not wired) — the modal
    // hides the dropdown via its `agentControlHosts &&` guard only when
    // undefined, so we keep [] to show the "no hosts" hint instead.
    const loadAgentControlHosts = useCallback(async () => {
        try {
            const res = await fetcher('/v1/admin/ai-agent-control-hosts');
            setAgentControlHosts(res.hosts ?? []);
        }
        catch {
            setAgentControlHosts([]);
        }
    }, [fetcher]);
    // The read-only Agent-Control hosts panel reads this state. It used to be
    // filled ONLY when a provider modal opened, so on a fresh page load the
    // panel always said "0 registered" even with both hosts online and
    // heartbeating (2026-09-04). NOTE the placement: this effect has to sit
    // AFTER the callback's `const`, because the deps array is evaluated during
    // render and would otherwise hit the temporal dead zone.
    useEffect(() => {
        void loadAgentControlHosts();
    }, [loadAgentControlHosts]);
    const openCreateProvider = useCallback(() => {
        setProviderDraft(emptyProviderDraft());
        setProviderFormBanner(null);
        setProviderFieldErrors({});
        setProviderGeneralErrors([]);
        setPickedCredentialId('');
        setPickedAgentControlHostId('');
        setProviderEdit({ kind: 'create' });
        void loadLlmProviderCreds();
        void loadAgentControlHosts();
    }, [loadLlmProviderCreds, loadAgentControlHosts]);
    const openEditProvider = useCallback((row) => {
        setProviderDraft(providerToDraft(row));
        setProviderFormBanner(null);
        setProviderFieldErrors({});
        setProviderGeneralErrors([]);
        // Hoist apiKeyCredentialId out of row.config (if present) — the
        // dropdown is the source of truth while the modal is open.
        const cfg = (row.config ?? {});
        const picked = typeof cfg.apiKeyCredentialId === 'string' ? cfg.apiKeyCredentialId : '';
        setPickedCredentialId(picked);
        // Same hoist for the agent_control host id (config.hostId).
        setPickedAgentControlHostId(typeof cfg.hostId === 'string' ? cfg.hostId : '');
        setProviderEdit({ kind: 'edit', row });
        void loadLlmProviderCreds();
        void loadAgentControlHosts();
    }, [loadLlmProviderCreds, loadAgentControlHosts]);
    const closeProviderEdit = useCallback(() => {
        if (providerSubmitting)
            return;
        setProviderEdit({ kind: 'closed' });
        setProviderFormBanner(null);
        setProviderFieldErrors({});
        setProviderGeneralErrors([]);
    }, [providerSubmitting]);
    const updateProviderDraftField = useCallback((key, value) => {
        setProviderDraft((d) => ({ ...d, [key]: value }));
        setProviderFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const applyProviderServerErrors = useCallback((err) => {
        if (err.code === 'INVALID_REQUEST' && Array.isArray(err.errors)) {
            const byField = {};
            const general = [];
            for (const e of err.errors) {
                const key = providerErrorFieldKey(e.field ?? e.path);
                if (key) {
                    (byField[key] ??= []).push(e.message);
                }
                else {
                    general.push(e.message);
                }
            }
            setProviderFieldErrors(byField);
            setProviderGeneralErrors(general);
            setProviderFormBanner(err.message || 'provider shape failed validation');
            return;
        }
        if (err.code === 'INVALID_REQUEST' && err.body?.field) {
            const fe = err.body;
            const key = providerErrorFieldKey(fe.field);
            if (key) {
                setProviderFieldErrors({ [key]: [err.message] });
            }
            else {
                setProviderGeneralErrors([err.message]);
            }
            setProviderFormBanner(err.message);
            return;
        }
        if (err.status === 409) {
            setProviderFormBanner(err.message || 'name already in use');
            return;
        }
        setProviderFormBanner(`${err.code}: ${err.message}`);
    }, []);
    const handleProviderSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (providerEdit.kind === 'closed')
            return;
        const name = providerDraft.name.trim();
        if (!isValidProviderName(name)) {
            setProviderFieldErrors((fe) => ({
                ...fe,
                name: ['Must match /^[a-z0-9][a-z0-9._-]{1,99}$/i.'],
            }));
            return;
        }
        const cfgParsed = parseConfigText(providerDraft.configText);
        if (!cfgParsed.ok) {
            setProviderFieldErrors((fe) => ({
                ...fe,
                configText: [cfgParsed.error],
            }));
            return;
        }
        // P1-AI14 — merge the picked credentialId into config (or remove
        // it when the operator picked "use legacy env-var"). Only applies
        // to anthropic + openai providers; other kinds get the config blob as-is.
        let mergedConfig = cfgParsed.config;
        if (providerDraft.kind === 'anthropic' || providerDraft.kind === 'openai' || providerDraft.kind === 'gemini' || providerDraft.kind === 'bedrock') {
            const next = { ...(mergedConfig ?? {}) };
            if (pickedCredentialId) {
                next.apiKeyCredentialId = pickedCredentialId;
            }
            else {
                delete next.apiKeyCredentialId;
            }
            mergedConfig = Object.keys(next).length > 0 ? next : null;
        }
        // agent_control — merge the picked host_devices id into config.hostId
        // (the adapter's required key; the host_id column stays null).
        if (providerDraft.kind === 'agent_control') {
            if (!pickedAgentControlHostId) {
                setProviderFormBanner('Pick an Agent-Control host — kind=agent_control requires config.hostId.');
                return;
            }
            mergedConfig = {
                ...(mergedConfig ?? {}),
                hostId: pickedAgentControlHostId,
            };
        }
        setProviderSubmitting(true);
        setProviderFormBanner(null);
        setProviderFieldErrors({});
        setProviderGeneralErrors([]);
        try {
            if (providerEdit.kind === 'create') {
                await fetcher('/v1/admin/ai-chat-providers', {
                    method: 'POST',
                    json: draftToCreateProvider(providerDraft, mergedConfig),
                });
                fireToast('success', `Added provider "${name}".`);
            }
            else {
                const patch = draftToPatchProvider(providerDraft, mergedConfig, providerEdit.row);
                if (Object.keys(patch).length === 0) {
                    setProviderEdit({ kind: 'closed' });
                    return;
                }
                await fetcher(`/v1/admin/ai-chat-providers/${encodeURIComponent(providerEdit.row.id)}`, { method: 'PATCH', json: patch });
                const wasDeployManaged = providerEdit.row.deployScriptManaged;
                fireToast('success', wasDeployManaged
                    ? `Saved "${name}". Ownership flipped to ui — deploy script will skip this row.`
                    : `Saved "${name}".`);
            }
            setProviderEdit({ kind: 'closed' });
            await loadProviders();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                applyProviderServerErrors(err);
            }
            else {
                setProviderFormBanner(err instanceof Error ? err.message : 'request failed (unknown)');
            }
        }
        finally {
            setProviderSubmitting(false);
        }
    }, [
        applyProviderServerErrors,
        fetcher,
        fireToast,
        loadProviders,
        providerDraft,
        providerEdit,
        pickedCredentialId,
        pickedAgentControlHostId,
    ]);
    const openProviderDelete = useCallback((row) => {
        setProviderDelete({ kind: 'open', row, submitting: false });
    }, []);
    const closeProviderDelete = useCallback(() => {
        setProviderDelete((d) => d.kind === 'open' && d.submitting ? d : { kind: 'closed' });
    }, []);
    const handleProviderDelete = useCallback(async () => {
        if (providerDelete.kind !== 'open')
            return;
        const row = providerDelete.row;
        setProviderDelete({ kind: 'open', row, submitting: true });
        try {
            await fetcher(`/v1/admin/ai-chat-providers/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
            fireToast('success', `Deleted provider "${row.name}".`);
            setProviderDelete({ kind: 'closed' });
            await loadProviders();
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'delete failed';
            fireToast('danger', msg);
            setProviderDelete({ kind: 'open', row, submitting: false });
        }
    }, [fetcher, fireToast, loadProviders, providerDelete]);
    // -------------------- derived --------------------------------------
    const sortedHosts = useMemo(() => {
        if (!hosts)
            return [];
        return [...hosts].sort((a, b) => a.name.localeCompare(b.name));
    }, [hosts]);
    /** P1-AI12 — derive count map from the allowed-by-host cache so the
     *  drawer's "Allowed models…" button can show how many models are
     *  whitelisted without re-fetching. (Was a row badge until the
     *  2026-09-04 consolidation moved that button into the drawer.) */
    const allowedModelCounts = useMemo(() => {
        const out = new Map();
        for (const [hostId, names] of allowedByHost.entries()) {
            out.set(hostId, names.length);
        }
        return out;
    }, [allowedByHost]);
    const totalHosts = sortedHosts.length;
    const totalProviders = providers?.length ?? 0;
    /** Opening Manage also seeds the host-edit draft: the drawer's
     *  Settings tab edits this row through the same draft + submit path
     *  the old Edit modal used (2026-09-04 consolidation). */
    const openDetail = useCallback((row) => {
        openEditHost(row);
        setDetail({ kind: 'open', row });
    }, [openEditHost]);
    /** Models tab — patch one provider's model in place. */
    const handleProviderModelChange = useCallback(async (provider, model) => {
        try {
            await fetcher(`/v1/admin/ai-chat-providers/${encodeURIComponent(provider.id)}`, { method: 'PATCH', json: { model } });
            fireToast('success', provider.deployScriptManaged
                ? `"${provider.name}" now uses ${model}. Ownership flipped to ui — the deploy script will skip this row.`
                : `"${provider.name}" now uses ${model}.`);
            await loadProviders();
        }
        catch (err) {
            fireToast('danger', err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'could not change the provider model');
        }
    }, [fetcher, fireToast, loadProviders]);
    // -------------------- render ---------------------------------------
    return (_jsxs("div", { children: [_jsx("h1", { className: "h3 mb-3", "data-testid": "ai-section-title", children: SECTION_TITLE[section].title }), _jsx("p", { className: "text-muted small mb-4", children: SECTION_TITLE[section].blurb }), section !== 'service' && section !== 'all' && _jsx(ProviderQuotaBannerLoader, {}), _jsxs("div", { hidden: !show('service'), children: [_jsx(AiServicePanel, { onToast: fireToast, onMode: onAiServiceMode }), aiService?.mode === 'remote' && _jsx(AppsAiResolutionPanel, { endpoint: aiService.endpoint, relayName: aiService.providerName }), show('service') && _jsx(ProviderQuotaPanel, { onToast: fireToast })] }), plumbingReadOnly && routingManagedElsewhere && _jsx(RoutedToNexusAiNotice, { endpoint: aiService?.endpoint ?? null }), _jsxs(LocalPlumbing, { collapsed: aiService?.mode === 'remote', children: [show('hosts') && _jsxs(_Fragment, { children: [_jsxs(ReadOnlyWhen, { on: plumbingReadOnly, children: [_jsx("div", { className: "card mb-3", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row g-2 align-items-end", children: [_jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", children: "Capability" }), _jsxs("select", { className: "form-select form-select-sm", value: capabilityFilter, onChange: (e) => setCapabilityFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All capabilities" }), _jsx("option", { value: "ollama-host", children: "ollama-host" }), _jsx("option", { value: "cli-host", children: "cli-host" }), _jsx("option", { value: "hybrid", children: "hybrid (both)" })] })] }), _jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", children: "Status" }), _jsxs("select", { className: "form-select form-select-sm", value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All statuses" }), _jsx("option", { value: "active", children: "active" }), _jsx("option", { value: "draining", children: "draining" }), _jsx("option", { value: "archived", children: "archived" })] })] }), _jsxs("div", { className: "col-md-6 d-flex justify-content-end gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => {
                                                                    void loadHosts();
                                                                    void loadProviders();
                                                                }, disabled: hosts === null, children: "Refresh" }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreateHost, children: "Add host" })] })] }) }) }), _jsx("div", { className: "card mb-3", children: _jsxs("div", { className: "card-body py-2", children: [_jsxs("div", { className: "row g-2 align-items-center", children: [_jsxs("div", { className: "col-md-4", children: [_jsx("label", { className: "form-label small mb-1", children: "App context" }), _jsxs("select", { className: "form-select form-select-sm", value: selectedAppId ?? '', onChange: (e) => {
                                                                        const v = e.target.value || null;
                                                                        setSelectedAppId(v);
                                                                        if (typeof window !== 'undefined') {
                                                                            const sp = new URLSearchParams(window.location.search);
                                                                            if (v)
                                                                                sp.set('app', v);
                                                                            else
                                                                                sp.delete('app');
                                                                            const qs = sp.toString();
                                                                            window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
                                                                        }
                                                                    }, disabled: appsList === null, children: [_jsx("option", { value: "", children: "\u2014 no app \u2014 (free-form model field)" }), (appsList ?? [])
                                                                            .filter((a) => a.status === 'active')
                                                                            .map((a) => (_jsxs("option", { value: a.appId, children: [a.displayName, " (", a.appId, ")"] }, a.appId)))] })] }), _jsx("div", { className: "col-md-8 small text-muted", children: selectedAppId
                                                                ? `Provider modal Model field becomes a dropdown when the picked host has allowed-models defined for "${selectedAppId}". Use each host's "Allowed models" button to edit the whitelist.`
                                                                : 'Pick an app to enable per-(app, host) allowed-model gating + the model-dropdown on the provider modal.' })] }), selectedAppId && (_jsxs("div", { className: "row g-2 align-items-end mt-1", children: [_jsxs("div", { className: "col-md-4", children: [_jsx("label", { className: "form-label small mb-1", children: "Chat provider for this app" }), _jsxs("select", { className: "form-select form-select-sm", value: appPinDraft ?? '', onChange: (e) => setAppPinDraft(e.target.value || null), disabled: providers === null || appPinBusy || routingManagedElsewhere, title: routingManagedElsewhere ? 'Not used while another instance manages AI (AI › Gateway default service)' : undefined, children: [_jsx("option", { value: "", children: "\u2014 inherit instance default \u2014" }), (providers ?? [])
                                                                            .filter((pr) => pr.enabled)
                                                                            .map((pr) => (_jsxs("option", { value: pr.name, children: [pr.name, " (", pr.model, ")"] }, pr.name)))] })] }), _jsx("div", { className: "col-md-2", children: _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: () => void saveAppPin(), disabled: appPinBusy || appPinDraft === appPin || (routingManagedElsewhere && appPinDraft !== null), children: appPinBusy ? 'Saving…' : 'Save routing' }) }), routingManagedElsewhere ? (_jsxs("div", { className: "col-md-6 small text-warning", children: ["Not used for inference: AI \u203A Gateway sends every app on this instance to the managing instance", aiService?.endpoint ? _jsxs(_Fragment, { children: [" (", _jsx("code", { children: aiService.endpoint }), ")"] }) : null, ", which applies its own chains. Set per-app routing there. You can only clear a saved routing here."] })) : (_jsxs("div", { className: "col-md-6 small text-muted", children: ["Routes /v1/chat for \u201C", selectedAppId, "\u201D only \u2014 every other app keeps the instance default. Failover ranks and allowed-model gating still apply. Pick a provider row that carries the host + model you want (add one above if needed)."] })), _jsx("div", { className: "col-12 small", children: appPinLoadError ? (_jsxs("span", { className: "text-danger", children: ["Couldn\u2019t load the saved routing (", appPinLoadError, ") \u2014 the select above does not reflect what the gateway uses."] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-muted", children: "Saved routing: " }), _jsx("code", { children: appPin ?? '— inherit instance default —' }), routingManagedElsewhere && appPin !== null && (_jsx("span", { className: "text-warning ms-2", children: "\u25CF ignored: the Gateway default service answers this app" })), appPinDraft !== appPin && (_jsx("span", { className: "text-warning ms-2", children: "\u25CF unsaved change \u2014 chat still uses the saved routing until you click Save routing" }))] })) })] }))] }) })] }), toast && (_jsxs("div", { className: `alert alert-${toast.kind} d-flex justify-content-between align-items-center`, children: [_jsx("span", { children: toast.message }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Dismiss", onClick: () => setToast(null) })] })), _jsxs(ReadOnlyWhen, { on: plumbingReadOnly, children: [_jsxs("div", { className: "card mb-4", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("span", { className: "fw-semibold", children: "Hosts" }), _jsx("span", { className: "text-muted small", children: hosts === null
                                                            ? 'Loading…'
                                                            : `${totalHosts} host${totalHosts === 1 ? '' : 's'}` })] }), hosts === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading AI hosts\u2026"] })), hosts !== null && loadHostsError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: loadHostsError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadHosts(), children: "Retry" })] })), hosts !== null && loadHostsError === null && totalHosts === 0 && (_jsxs("div", { className: "card-body text-center text-muted", children: [_jsx("p", { className: "mb-2", children: "No AI hosts registered yet." }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreateHost, children: "Add host" })] })), hosts !== null && loadHostsError === null && totalHosts > 0 && (_jsx(HostsTable, { hosts: sortedHosts, workspaceCounts: workspaceCounts, providers: providers ?? [], now: now, onOpenDetail: openDetail, onArchive: openArchive }))] }), _jsx(AppPriorityPanel, {}), _jsx(WorkerRecoveryPanel, {})] })] }), show('agents') && _jsx(_Fragment, { children: _jsxs("div", { className: "card mb-4", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("span", { className: "fw-semibold", children: "Agent-Control hosts" }), ' ', _jsxs("span", { className: "text-muted small", children: ["(", agentControlHosts.length, " registered \u2014 self-register on AI-Control sign-in; configure on the host via tray \u2192 Settings)"] })] }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void loadAgentControlHosts(), children: "Refresh" })] }), _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm align-middle mb-0", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Platform" }), _jsx("th", { children: "Version" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Last seen" }), _jsx("th", { children: "Chat sessions" }), _jsx("th", { children: "Workspaces" }), _jsx("th", { children: "Max runs" })] }) }), _jsxs("tbody", { children: [agentControlHosts.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 8, className: "text-muted small", children: "No Agent-Control hosts registered \u2014 install the AI-Control app on a machine and sign in; it appears here within one heartbeat." }) })), agentControlHosts.map((h) => (_jsxs("tr", { children: [_jsx("td", { className: "fw-semibold", children: h.name }), _jsx("td", { children: _jsx("span", { className: "badge text-bg-info", children: h.platform }) }), _jsx("td", { className: "font-monospace small", children: h.appVersion ?? '—' }), _jsx("td", { children: h.online ? (_jsx("span", { className: "badge text-bg-success", children: "online" })) : (_jsx("span", { className: "badge text-bg-secondary", children: "offline" })) }), _jsx("td", { className: "small text-muted", children: h.lastSeenAt
                                                                    ? new Date(h.lastSeenAt).toLocaleString()
                                                                    : 'never' }), _jsx("td", { className: "small", children: h.chatSessionsCapable
                                                                    ? `capable · ${h.activeChatSessions ?? 0} active`
                                                                    : '—' }), _jsx("td", { className: "small font-monospace", children: (h.workspaces ?? []).join(', ') || '—' }), _jsx("td", { className: "small", children: h.maxConcurrentRuns ?? '—' })] }, h.id)))] })] }) })] }) }), show('service') && _jsxs(_Fragment, { children: [providers !== null && _jsx(FallbackPathsPanel, { providers: providers, onToast: fireToast }), _jsx(ExecutionPolicyPanel, { apps: appsList }), providers !== null && (_jsx(ImageServicesPanel, { providers: providers, hosts: hosts ?? [], onToast: fireToast })), providers !== null && (_jsx(VoiceServicesPanel, { providers: providers, hosts: hosts ?? [], onToast: fireToast }))] }), show('agents') && _jsxs(_Fragment, { children: [_jsx(RunnersPanel, { onToast: fireToast }), section === 'all' && _jsx(SubscriptionUsagePanel, { onToast: fireToast })] }), show('providers') && _jsx(_Fragment, { children: _jsx(ReadOnlyWhen, { on: plumbingReadOnly, children: _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("span", { className: "fw-semibold", children: "Chat providers" }), ' ', _jsxs("span", { className: "text-muted small", children: ["(", totalProviders, " configured)"] })] }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreateProvider, children: "Add provider" })] }), providers === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading chat providers\u2026"] })), providers !== null && loadProvidersError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: loadProvidersError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadProviders(), children: "Retry" })] })), providers !== null && loadProvidersError === null && (_jsx("div", { className: "card-body", children: _jsx(ProvidersTable, { providers: providers, hosts: hosts ?? [], onEdit: openEditProvider, onDelete: openProviderDelete, onRefresh: loadProviders, onToast: fireToast }) }))] }) }) }), hostEdit.kind === 'create' && (_jsx(HostEditModal, { mode: hostEdit.kind, original: null, draft: hostDraft, fieldErrors: hostFieldErrors, generalErrors: hostGeneralErrors, formBanner: hostFormBanner, submitting: hostSubmitting, onChange: updateHostDraftField, onCancel: closeHostEdit, onSubmit: handleHostSubmit })), detail.kind === 'open' && (_jsx(HostDetailDrawer, { host: detail.row, providers: providers ?? [], onClose: () => {
                            setDetail({ kind: 'closed' });
                            setHostEdit({ kind: 'closed' });
                            void loadHosts();
                        }, onToast: fireToast, onInstallOllama: (row) => setDeploy({ kind: 'open', row }), onAllowedModels: selectedAppId
                            ? (row) => setAllowedPicker({
                                kind: 'open',
                                host: row,
                                appId: selectedAppId,
                            })
                            : undefined, allowedModelCount: allowedModelCounts.get(detail.row.id), onProviderModelChange: handleProviderModelChange, settings: {
                            draft: hostDraft,
                            fieldErrors: hostFieldErrors,
                            generalErrors: hostGeneralErrors,
                            formBanner: hostFormBanner,
                            submitting: hostSubmitting,
                            onChange: updateHostDraftField,
                            onSubmit: handleHostSubmit,
                        } })), deploy.kind === 'open' && (_jsx(DeployOllamaModal, { host: deploy.row, onClose: () => {
                            setDeploy({ kind: 'closed' });
                            void loadHosts();
                        }, onToast: fireToast })), archive.kind === 'open' && (_jsxs(ConfirmModal, { title: "Archive AI host", confirmLabel: "Archive", confirmVariant: "danger", submitting: archive.submitting, onCancel: closeArchive, onConfirm: () => void handleArchive(), children: [_jsxs("p", { className: "mb-2", children: ["Archive host ", _jsx("code", { children: archive.row.name }), "?"] }), _jsxs("p", { className: "small text-muted mb-0", children: ["This is a soft delete (sets ", _jsx("code", { children: "status='archived'" }), "). Workspaces + providers stay attached so history is preserved.", archive.row.deployScriptManaged && (_jsxs(_Fragment, { children: [' ', "Note: this row is currently deploy-managed \u2014 the next re-deploy will revive it unless removed from the config."] }))] })] })), providerEdit.kind !== 'closed' && (_jsx(ProviderEditModal, { mode: providerEdit.kind, original: providerEdit.kind === 'edit' ? providerEdit.row : null, hosts: hosts ?? [], claudeModels: claudeModels, credentials: llmProviderCreds.map((c) => ({
                            id: c.id,
                            name: c.name,
                            identityRef: c.identityRef,
                            fingerprint: c.fingerprint,
                        })), apiKeyCredentialId: pickedCredentialId, onApiKeyCredentialIdChange: setPickedCredentialId, agentControlHosts: agentControlHosts, agentControlHostId: pickedAgentControlHostId, onAgentControlHostIdChange: setPickedAgentControlHostId, draft: providerDraft, fieldErrors: providerFieldErrors, generalErrors: providerGeneralErrors, formBanner: providerFormBanner, submitting: providerSubmitting, onChange: updateProviderDraftField, onCancel: closeProviderEdit, onSubmit: handleProviderSubmit, appId: selectedAppId, allowedModelsForCurrentHost: 
                        // The dropdown only gets a list when (a) we have an app
                        // selected AND (b) the draft has a host picked. Cloud
                        // providers (anthropic, no host_id) pass through as empty
                        // -> stays free-form, same as today.
                        selectedAppId && providerDraft.hostId
                            ? allowedByHost.get(providerDraft.hostId) ?? []
                            : [] })), allowedPicker.kind === 'open' && (_jsx(AllowedModelsPicker, { appId: allowedPicker.appId, host: allowedPicker.host, onClose: () => setAllowedPicker({ kind: 'closed' }), onSaved: () => {
                            // Refresh the in-memory cache so the provider modal
                            // dropdown picks up the new whitelist immediately.
                            if (selectedAppId && hosts) {
                                void loadAllowedByHost(hosts, selectedAppId);
                            }
                        }, onToast: fireToast })), providerDelete.kind === 'open' && (_jsxs(ConfirmModal, { title: "Delete chat provider", confirmLabel: "Delete", confirmVariant: "danger", submitting: providerDelete.submitting, onCancel: closeProviderDelete, onConfirm: () => void handleProviderDelete(), children: [_jsxs("p", { className: "mb-2", children: ["Delete provider ", _jsx("code", { children: providerDelete.row.name }), "?"] }), _jsx("p", { className: "small text-muted mb-0", children: "This is a hard delete \u2014 there's no archive state for providers. Any /v1/chat call that referenced this name will start failing with 404." })] }))] })] }));
}
function ConfirmModal({ title, confirmLabel, confirmVariant, submitting, onCancel, onConfirm, children, }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: title }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onCancel, disabled: submitting })] }), _jsx("div", { className: "modal-body", children: children }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "button", className: `btn btn-${confirmVariant}`, onClick: onConfirm, disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Working\u2026"] })) : (confirmLabel) })] })] }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
//# sourceMappingURL=AiHostsPage.js.map